import { Injectable, inject } from '@angular/core';

import { Observable, of } from 'rxjs';

import { ExtendedTab, GlobalCareNoteRoutes, GlobalMyAccountProfileRoutes } from '@hdms/shared/common-types';

import { AccessService } from './access.service';

interface EnabledExtendedTab extends ExtendedTab {
  enabled?: (accessService: AccessService) => boolean;
}

enum ProfileHeaderNavItem {
  PROFILE = 'profile',
  LOGIN_DATA = 'loginData',
  CONTACT_DATA = 'contactData',
  COMPANY_DATA = 'companyData',
  SUBSCRIPTION_PREFERENCES = 'subscriptionPreferences',
  DELIVERY_ADDRESSES = 'deliveryAddresses',
  CUSTOMER_CARE_NOTES = 'customerCareNotes'
}
const profileBaseRoute = `/${GlobalMyAccountProfileRoutes.BASE}`;

const PROFILE_HEADER_LINKS: EnabledExtendedTab[] = [
  {
    id: ProfileHeaderNavItem.PROFILE,
    url: profileBaseRoute,
    label: ProfileHeaderNavItem.PROFILE
  },
  {
    id: ProfileHeaderNavItem.LOGIN_DATA,
    url: profileBaseRoute,
    label: ProfileHeaderNavItem.LOGIN_DATA
  },
  {
    id: ProfileHeaderNavItem.CONTACT_DATA,
    url: profileBaseRoute,
    label: ProfileHeaderNavItem.CONTACT_DATA
  },
  {
    id: ProfileHeaderNavItem.COMPANY_DATA,
    url: profileBaseRoute,
    label: ProfileHeaderNavItem.COMPANY_DATA
  },
  {
    id: ProfileHeaderNavItem.SUBSCRIPTION_PREFERENCES,
    url: `${profileBaseRoute}#account-subscription-data`,
    label: ProfileHeaderNavItem.SUBSCRIPTION_PREFERENCES
  },
  {
    id: ProfileHeaderNavItem.DELIVERY_ADDRESSES,
    url: `${profileBaseRoute}/${GlobalMyAccountProfileRoutes.DELIVERY_ADDRESS_LIST}`,
    label: ProfileHeaderNavItem.DELIVERY_ADDRESSES,
    enabled: a => a.isDeliveryAddressManagementEnabled
  },
  {
    id: ProfileHeaderNavItem.CUSTOMER_CARE_NOTES,
    url: `/${GlobalCareNoteRoutes.BASE}`,
    label: ProfileHeaderNavItem.CUSTOMER_CARE_NOTES,
    enabled: a => !a.isByDesign && a.isAccountManager
  }
];

/**
 * Purpose of this service is to centralize the primary link configuration for entering
 * MyAccountProfile feature, displayed in a future ribbon tab set as well as user account menu in header component.
 * Both have to rely on one single source of truth based on same permission conditions.
 * As so far there don't exist any permission-based conditions, service just returns
 * a static array of tab links. In case this would change in future, the conditions should be added
 * by implementing an access service, which then also has to be used in feature route-guard.
 */
@Injectable({
  providedIn: 'root'
})
export class ProfileNavigationService {
  private readonly accessService = inject(AccessService);

  getNavigation$(): Observable<ExtendedTab[]> {
    return of(this.getAvailableHeaderLinks());
  }

  private getAvailableHeaderLinks(): ExtendedTab[] {
    return PROFILE_HEADER_LINKS.filter(link => !link.enabled || link.enabled(this.accessService));
  }
}
